// Copyright ©️ 2026 eVolve MEP, LLC

import { useMemo } from 'react';

import type { EventsByFeature } from '@evolvemep/api-ts';
import type { AgChartOptions } from 'ag-charts-enterprise';

import { isNil, isNotNil } from 'helpers/isNotNil';
import { ChartRenderer } from 'modules/Shop/Insights/RevitUsage/components/ChartRenderer';
import type { AnalyticsChartProps } from 'modules/Shop/Insights/RevitUsage/types';
import { useChartFilters } from 'modules/Shop/Insights/RevitUsage/useChartFilters';
import { useAnalyticsChart } from 'modules/Shop/Insights/RevitUsage/views/charts/useAnalyticsChart';

export const FeatureUsageChart = ({ showDemo, ...props }: AnalyticsChartProps<EventsByFeature[]>) => {
  const filters = useChartFilters();
  const [allData, loading] = useAnalyticsChart(`usage-stats/v1/reporting/events/features`, props);

  const maxDisplayRecords = useMemo(
    () => (props.usedFilters.includes('featureTypeNames') && filters.featureTypeNames.length > 0 ? null : 20),
    [filters.featureTypeNames.length, props.usedFilters],
  );

  const [filteredData, limitReason] = useMemo(() => {
    if (!allData) return [null, null];
    if (isNil(maxDisplayRecords) || allData.length <= maxDisplayRecords) return [allData, null];

    const minUsage = 10;
    if (allData.filter((d) => d.totalUsage >= minUsage).length > maxDisplayRecords / 2) {
      return [allData.filter((d) => d.totalUsage >= minUsage), `Limited to features with at least ${minUsage} events`];
    }
    return [
      allData.toSorted((a, b) => b.totalUsage - a.totalUsage).slice(0, maxDisplayRecords),
      // TODO: This branch seems unreachable; investigate
      // oxlint-disable-next-line typescript/restrict-template-expressions
      `Limited to top ${maxDisplayRecords} features`,
    ];
  }, [allData, maxDisplayRecords]);

  const options = useMemo<AgChartOptions>(
    () => ({
      data: [
        {
          totalUsage: allData?.reduce((out, curr) => out + curr.totalUsage, 0) ?? 0,
          children: filteredData?.map((d) => ({
            ...d,
            // Without this, the parent object influences the color range
            totalUsageColor: d.totalUsage,
          })),
        },
      ],
      title: {
        enabled: !showDemo,
        text: 'Event Heatmap',
        textAlign: 'left',
      },
      subtitle: {
        text: limitReason ?? '',
        textAlign: 'left' as const,
        enabled: isNotNil(limitReason) && !showDemo,
      },
      series: [
        {
          type: 'treemap',
          labelKey: 'featureTypeName',
          sizeKey: 'totalUsage',
          fills: ['#ddd'],
          colorKey: 'totalUsageColor',
          colorRange: ['#13d6d6', '#3eb01e'],
          tooltip: {
            renderer: ({ datum }: { datum: EventsByFeature }) =>
              'children' in datum
                ? // Don't render a tooltip for the parent hover
                  ''
                : {
                    data: [
                      {
                        label: 'Total Events',
                        value: `${datum.totalUsage}`,
                      },
                    ],
                  },
          },
        },
      ],
      gradientLegend: {
        enabled: false,
      },
    }),
    [allData, filteredData, limitReason, showDemo],
  );

  return <ChartRenderer withPadding={false} options={options} loading={loading} />;
};
